import posthog from '@/lib/posthogWrapper';
import { loadScript } from './scriptLoader';
import * as Sentry from '@sentry/nextjs';
import { LandingError } from '@/types/errors';
import { TrackingParams } from '@/types';
export const fetchTIH = async (url: string) => {
	try {
		const landingResponse = await fetch(`/api/landing?url=${encodeURIComponent(url)}`);
		const data = await landingResponse.json();

		posthog.capture('landing-api-response', {
			success: landingResponse.ok,
			url,
		});

		return data;
	} catch (err) {
		posthog.capture('landing-api-response', {
			success: false,
			url,
		});

		Sentry.withScope((scope) => {
			scope.setTag('api', 'landingAPI');
			scope.setLevel('error');
			Sentry.captureException(new LandingError(err as Error));
		});

		return {};
	}
};

export const isGtmLoaded = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	window.dataLayer = window.dataLayer || [];
	const gtmStartedEvent = window.dataLayer.find((element) => element?.event === 'gtm.load');

	if (!gtmStartedEvent) {
		return false; // Not even the GTM inline config has executed
	} else if (!gtmStartedEvent['gtm.uniqueEventId']) {
		// GTM inline config has ran, but main GTM js is not loaded
		// (likely AdBlock, NoScript, URL blocking etc.)
		return false;
	}

	return true; // GTM is fully loaded and working
};

export const getTrackingParams = (): TrackingParams => {
	const currentParams = new URLSearchParams(window.location.search);

	return {
		fbclid: currentParams.get('fbclid') || '',
		gclid: currentParams.get('gclid') || '',
		wbraid: currentParams.get('wbraid') || '',
		gbraid: currentParams.get('gbraid') || '',
		rdt_cid: currentParams.get('rdt_cid') || '',
	};
};

export const loadGTM = (tag: string) => {
	// Skip GTM loading in CI environments
	if (process.env.CI || process.env.VERCEL_ENV === 'preview') {
		// eslint-disable-next-line no-console
		console.log('Skipping GTM load in CI/test environment');
		return;
	}

	window.dataLayer = window.dataLayer || [];

	// Push GTM initialization event
	window.dataLayer.push({
		'gtm.start': new Date().getTime(),
		event: 'gtm.js',
	});

	loadScript(
		{
			src: `https://www.googletagmanager.com/gtm.js?id=${tag}`,
			bundleId: tag,
			mustBeFirst: true,
			retry: true,
		},
		tag,
	);
};

export const pushVWO = () => {
	// Track VWO initialization
	const vwoLoaded = !!window.VWO && !!window._vwo_code;

	posthog.capture('script_load_response', {
		success: vwoLoaded,
		script: 'vwo',
		// @ts-expect-error: vwo hates typescript
		value: window._vwo_exp ? JSON.stringify(Object.keys(window._vwo_exp)) : null,
	});

	window.VWO = window.VWO || [];
	window.VWO?.push([
		'onEventReceive',
		'vA',
		(vwoData: Array<string>) => {
			try {
				// To fetch A/B test id
				const experimentId = vwoData[1];
				// To fetch A/B test active variation name
				const variationId = vwoData[2];
				// @ts-expect-error: vwo hates typescript
				const abTestName = _vwo_exp[experimentId]?.name;
				// @ts-expect-error: vwo hates typescript
				const variationName = _vwo_exp[experimentId]?.comb_n?.[variationId];

				const isValidAssignment =
					// @ts-expect-error: vwo hates typescript
					typeof _vwo_exp[experimentId]?.comb_n?.[variationId] !== 'undefined' &&
					[
						'VISUAL_AB',
						'VISUAL',
						'SPLIT_URL',
						'SURVEY',
						// @ts-expect-error: vwo hates typescript
					].indexOf(_vwo_exp[experimentId].type) > -1;

				// Track VWO experiment assignment (success or failure)
				posthog.capture('script_load_response', {
					success: isValidAssignment,
					script: 'vwo',
					value: isValidAssignment
						? JSON.stringify({
								experiment_id: experimentId,
								experiment_name: abTestName,
								variation_id: variationId,
								variation_name: variationName,
								// @ts-expect-error: vwo hates typescript
								experiment_type: _vwo_exp[experimentId].type,
							})
						: JSON.stringify({
								experiment_id: experimentId,
								variation_id: variationId,
								error: 'invalid_experiment_data',
							}),
				});

				if (isValidAssignment) {
					const hidden = document.querySelector('#hidden-form-data');
					const i = document.createElement('input');
					const h = document.createElement('input');

					i.type = 'hidden';
					i.name = 'abTestName';
					i.id = 'abTestName';
					i.value = abTestName;

					h.type = 'hidden';
					h.name = 'variationName';
					h.id = 'variationName';
					h.value = variationName;

					hidden?.appendChild(i);
					hidden?.appendChild(h);

					if (window) {
						window.dataLayer = window.dataLayer || [];
						window.dataLayer.push({
							event: 'vwo_page_view',
							abTestName,
							variationName,
						});
						posthog.setPersonProperties({
							VWO_AB_TEST_NAME: abTestName,
							VWO_VARIATION_NAME: variationName,
							VWO_EXPERIMENT_ID: experimentId,
							VWO_VARIATION_ID: variationId,
						});
					}
				}
			} catch (error) {
				// Track VWO processing errors
				posthog.capture('script_load_response', {
					success: false,
					script: 'vwo',
					value: JSON.stringify({
						error: error instanceof Error ? error.message : 'Unknown VWO error',
						vwo_data_provided: !!vwoData,
						vwo_data_length: vwoData?.length || 0,
					}),
				});
			}
		},
	]);
};
